import { getPublicClient, multicall, readContract } from "@wagmi/core";
import { createPublicClient, formatUnits, http, parseAbiItem } from "viem";
import { mainnet } from "viem/chains";
import { erc721Abi, stakingAbi, stakingReadAbi, erc20Abi } from "./abis.js";
import { getMainnetRpcProxyAbsoluteUrl, getStakingEnv, isHexAddress } from "./env.js";
import { fetchOwnedTokenIds } from "./alchemyNfts.js";

/** Matches `OwlexStaking.STAKE_DURATION` (6 hours). */
export const STAKE_DURATION_SEC = 6n * 3600n;

const stakedEvent = parseAbiItem(
  "event Staked(address indexed user, uint256 indexed tokenId, uint64 stakedAt)"
);
const batchStakedEvent = parseAbiItem(
  "event BatchStaked(address indexed user, uint256[] tokenIds)"
);
const unstakedEvent = parseAbiItem(
  "event Unstaked(address indexed user, uint256 indexed tokenId, uint256 xpMinted)"
);
const emergencyEvent = parseAbiItem(
  "event EmergencyReturn(address indexed staker, uint256 indexed tokenId)"
);
const transferEvent = parseAbiItem(
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)"
);

/** Independent public RPCs for log reconciliation (do not rely on a single provider path). */
const PUBLIC_LOG_RPC_URLS = [
  "https://ethereum.publicnode.com",
  "https://cloudflare-eth.com",
];

/** Final Roost token IDs cache (TTL avoids repeated eth_getLogs on tab switches / minor UI events). */
const ROOST_IDS_CACHE_MS = 60_000;
/** @type {Map<string, { t: number, ids: string[] }>} */
const roostIdsCache = new Map();
/** Single-flight: duplicate concurrent roost rebuilds were multiplying RPC + Alchemy usage. */
/** @type {Map<string, Promise<{ tokenIds: string[], stakeRows: object[] }>>} */
const roostFetchInFlight = new Map();

/**
 * @param {`0x${string}`} user
 * @param {`0x${string}`} stakingAddr
 * @param {string} nftAddr
 */
function roostCacheKey(user, stakingAddr, nftAddr) {
  return `${user.toLowerCase()}:${stakingAddr.toLowerCase()}:${String(nftAddr || "").toLowerCase()}`;
}

/**
 * Clear cached staked token IDs. Call after stake/unstake and wallet disconnect.
 * @param {string | null} [owner] If set, only that wallet cache entries are removed.
 */
export function clearStakedTokenIdsCache(owner = null) {
  if (!owner) {
    roostIdsCache.clear();
    return;
  }
  const p = owner.toLowerCase() + ":";
  for (const k of roostIdsCache.keys()) {
    if (k.startsWith(p)) roostIdsCache.delete(k);
  }
}

/**
 * @param {bigint} from
 * @param {bigint} to
 */
function minBig(a, b) {
  return a < b ? a : b;
}

/**
 * @param {import('viem').PublicClient} publicClient
 * @param {import('viem').GetLogsParameters} filter
 * @param {{ latestBlock?: bigint }} [options] Pass `latestBlock` from a single `getBlockNumber()` to avoid extra RPC round-trips per scan.
 */
export async function getLogsChunked(publicClient, filter, options = {}) {
  const fromBlock =
    typeof filter.fromBlock === "bigint"
      ? filter.fromBlock
      : BigInt(filter.fromBlock ?? 0);

  const scanToTip = filter.toBlock === "latest" || filter.toBlock == null;
  /** @type {bigint | null} */
  let fixedToEnd = null;
  /** @type {bigint | null} */
  let tipSnapshot = null;

  if (filter.toBlock !== "latest" && filter.toBlock != null) {
    fixedToEnd =
      typeof filter.toBlock === "bigint"
        ? filter.toBlock
        : BigInt(filter.toBlock);
  } else if (options.latestBlock != null) {
    tipSnapshot = options.latestBlock;
  } else {
    tipSnapshot = await publicClient.getBlockNumber();
  }

  /** Inclusive end block for the bulk scan (never includes the tip block when tip > fromBlock). */
  let bulkEnd;
  if (fixedToEnd != null) {
    bulkEnd = fixedToEnd;
  } else if (tipSnapshot != null && tipSnapshot > fromBlock) {
    bulkEnd = tipSnapshot - 1n;
  } else {
    bulkEnd = tipSnapshot ?? fromBlock;
  }

  const CHUNK = 200_000n;
  const out = [];
  let from = fromBlock;
  while (from <= bulkEnd) {
    const to = minBig(from + CHUNK - 1n, bulkEnd);
    /** @type {bigint} */
    let coveredTo = to;
    let chunk;
    try {
      chunk = await publicClient.getLogs({
        ...filter,
        fromBlock: from,
        toBlock: to,
      });
    } catch (e) {
      const m = String(e?.shortMessage || e?.message || e || "");
      if (/beyond current head|extends beyond|head block/i.test(m) && to > from) {
        coveredTo = to - 1n;
        chunk = await publicClient.getLogs({
          ...filter,
          fromBlock: from,
          toBlock: coveredTo,
        });
      } else {
        throw e;
      }
    }
    out.push(...chunk);
    from = coveredTo + 1n;
  }

  // Large ranges ending at "latest" fail on some RPCs; we scan ..tip-1 above, then append the
  // tail (tip..current head) so stakes in the newest block(s) are never dropped.
  if (scanToTip && fixedToEnd == null && tipSnapshot != null) {
    const tailFrom = bulkEnd + 1n;
    try {
      const head = await publicClient.getBlockNumber();
      if (head >= tailFrom) {
        let tailChunk;
        try {
          tailChunk = await publicClient.getLogs({
            ...filter,
            fromBlock: tailFrom,
            toBlock: head,
          });
        } catch (e) {
          const m = String(e?.shortMessage || e?.message || e || "");
          if (/beyond current head|extends beyond|head block/i.test(m)) {
            if (head > tailFrom) {
              tailChunk = await publicClient.getLogs({
                ...filter,
                fromBlock: tailFrom,
                toBlock: head - 1n,
              });
            } else {
              try {
                tailChunk = await publicClient.getLogs({
                  ...filter,
                  fromBlock: tailFrom,
                  toBlock: "latest",
                });
              } catch {
                // Tip block not yet queryable on some RPCs; next dashboard refresh includes it in bulk.
                tailChunk = [];
              }
            }
          } else {
            throw e;
          }
        }
        out.push(...tailChunk);
      }
    } catch (e) {
      console.warn("[owlex-staking] getLogs tail (tip..head) failed", e);
    }
  }

  return out;
}

function deployBlockFromEnv() {
  const raw = String(import.meta.env.VITE_STAKING_DEPLOY_BLOCK ?? "").trim();
  if (!raw) return 24892219n;
  try {
    return BigInt(raw);
  } catch {
    return 24892219n;
  }
}

/** Max ERC-721 candidates to verify with `stakeInfo` in one refresh (safety cap). */
const MAX_STAKEINFO_CANDIDATES = 2000;

/**
 * @param {import('viem').PublicClient} pc
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 * @param {bigint} fromBlock
 */
async function fetchStakedTokenIdsFromLogsWithClient(pc, stakingAddr, user, fromBlock) {
  const latestBlock = await pc.getBlockNumber();
  const logOpts = { latestBlock };
  const [stakedLogs, batchStakedLogs, unstakedLogs, emergencyLogs] = await Promise.all([
    getLogsChunked(
      pc,
      {
        address: stakingAddr,
        event: stakedEvent,
        args: { user },
        fromBlock,
        toBlock: "latest",
      },
      logOpts
    ),
    getLogsChunked(
      pc,
      {
        address: stakingAddr,
        event: batchStakedEvent,
        args: { user },
        fromBlock,
        toBlock: "latest",
      },
      logOpts
    ),
    getLogsChunked(
      pc,
      {
        address: stakingAddr,
        event: unstakedEvent,
        args: { user },
        fromBlock,
        toBlock: "latest",
      },
      logOpts
    ),
    getLogsChunked(
      pc,
      {
        address: stakingAddr,
        event: emergencyEvent,
        args: { staker: user },
        fromBlock,
        toBlock: "latest",
      },
      logOpts
    ),
  ]);

  /** @type {Set<string>} */
  const ids = new Set();

  for (const log of stakedLogs) {
    const tid = log.args.tokenId;
    if (tid != null) ids.add(tid.toString());
  }
  for (const log of batchStakedLogs) {
    const arr = log.args.tokenIds;
    if (!arr) continue;
    for (const tid of arr) {
      if (tid != null) ids.add(tid.toString());
    }
  }
  for (const log of unstakedLogs) {
    const tid = log.args.tokenId;
    if (tid != null) ids.delete(tid.toString());
  }
  for (const log of emergencyLogs) {
    const tid = log.args.tokenId;
    if (tid != null) ids.delete(tid.toString());
  }

  return [...ids];
}

/**
 * Two distinct mainnet endpoints in parallel, merged (cheap vs 4× sequential full scans).
 * @param {import('@wagmi/core').Config} config
 */
function roostLogScanClients(config) {
  const proxyUrl = getMainnetRpcProxyAbsoluteUrl();
  const a = proxyUrl
    ? createPublicClient({
        chain: mainnet,
        transport: http(proxyUrl, { batch: false }),
      })
    : getPublicClient(config, { chainId: 1 }) ??
      createPublicClient({
        chain: mainnet,
        transport: http(PUBLIC_LOG_RPC_URLS[0], { batch: false }),
      });
  const b = createPublicClient({
    chain: mainnet,
    transport: http(PUBLIC_LOG_RPC_URLS[1], { batch: false }),
  });
  return [a, b];
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 * @param {bigint} fromBlock
 */
async function fetchStakedTokenIdsFromLogs(config, stakingAddr, user, fromBlock) {
  const clients = roostLogScanClients(config);
  /** @type {Set<string>} */
  const merged = new Set();
  const results = await Promise.allSettled(
    clients.map((pc) => fetchStakedTokenIdsFromLogsWithClient(pc, stakingAddr, user, fromBlock))
  );
  let lastErr = null;
  for (const r of results) {
    if (r.status === "fulfilled") {
      for (const id of r.value) merged.add(id);
    } else {
      lastErr = r.reason;
      console.warn("[owlex-staking] fetchStakedTokenIdsFromLogs (one endpoint failed)", r.reason);
    }
  }
  if (merged.size > 0) return [...merged];
  if (lastErr) {
    console.error("[owlex-staking] fetchStakedTokenIdsFromLogs failed for all endpoints", lastErr);
  }
  return [];
}

/**
 * Reconstruct active staked token IDs from ERC-721 Transfer logs:
 * user -> staking means "in", staking -> user means "out".
 * Uses configured staking deploy block to keep scans fast.
 * @param {import('viem').PublicClient} pc
 * @param {`0x${string}`} nftAddr
 * @param {`0x${string}`} user
 * @param {`0x${string}`} stakingAddr
 * @param {bigint} fromBlock
 */
async function fetchStakedTokenIdsFromNftTransfersWithClient(
  pc,
  nftAddr,
  user,
  stakingAddr,
  fromBlock
) {
  const [inLogs, outLogs] = await Promise.all([
    getLogsChunked(pc, {
      address: nftAddr,
      event: transferEvent,
      args: { from: user, to: stakingAddr },
      fromBlock,
      toBlock: "latest",
    }),
    getLogsChunked(pc, {
      address: nftAddr,
      event: transferEvent,
      args: { from: stakingAddr, to: user },
      fromBlock,
      toBlock: "latest",
    }),
  ]);

  /** @type {Set<string>} */
  const ids = new Set();
  for (const log of inLogs) {
    const tid = log.args.tokenId;
    if (tid != null) ids.add(tid.toString());
  }
  for (const log of outLogs) {
    const tid = log.args.tokenId;
    if (tid != null) ids.delete(tid.toString());
  }
  return [...ids];
}

/**
 * Same two-endpoint merge as staking logs.
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} nftAddr
 * @param {`0x${string}`} user
 * @param {`0x${string}`} stakingAddr
 * @param {bigint} fromBlock
 */
async function fetchStakedTokenIdsFromNftTransfers(
  config,
  nftAddr,
  user,
  stakingAddr,
  fromBlock
) {
  const clients = roostLogScanClients(config);
  /** @type {Set<string>} */
  const merged = new Set();
  const results = await Promise.allSettled(
    clients.map((pc) =>
      fetchStakedTokenIdsFromNftTransfersWithClient(pc, nftAddr, user, stakingAddr, fromBlock)
    )
  );
  let lastErr = null;
  for (const r of results) {
    if (r.status === "fulfilled") {
      for (const id of r.value) merged.add(id);
    } else {
      lastErr = r.reason;
      console.warn(
        "[owlex-staking] fetchStakedTokenIdsFromNftTransfers (one endpoint failed)",
        r.reason
      );
    }
  }
  if (merged.size > 0) return [...merged];
  if (lastErr) {
    console.warn("[owlex-staking] NFT Transfer reconstruction unavailable", lastErr);
  }
  return [];
}

/**
 * @param {string} apiKey
 * @param {Record<string, unknown>} params
 */
async function fetchTokenIdsFromAlchemyTransferDirection(apiKey, params) {
  const url = `https://eth-mainnet.g.alchemy.com/v2/${apiKey}`;
  /** @type {string[]} */
  const out = [];
  let pageKey;
  try {
    do {
      const reqParams = {
        ...params,
        category: ["erc721"],
        withMetadata: false,
        excludeZeroValue: false,
        maxCount: "0x3e8",
      };
      if (pageKey) reqParams.pageKey = pageKey;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method: "alchemy_getAssetTransfers",
          params: [reqParams],
        }),
      });
      if (!res.ok) break;
      const json = await res.json();
      if (json.error) {
        console.warn("[owlex-staking] alchemy_getAssetTransfers", json.error);
        break;
      }
      const transfers = json.result?.transfers ?? [];
      for (const t of transfers) {
        const raw = t.tokenId ?? t.erc721TokenId;
        if (raw == null) continue;
        try {
          out.push(BigInt(String(raw)).toString());
        } catch {
          out.push(String(raw));
        }
      }
      pageKey = json.result?.pageKey;
    } while (pageKey);
  } catch (e) {
    console.warn("[owlex-staking] fetchTokenIdsFromAlchemyTransferDirection", e);
  }
  return out;
}

/**
 * Reconstruct current staked token IDs from Alchemy transfer history:
 * user->staking add, staking->user remove.
 * @param {string} apiKey
 * @param {`0x${string}`} user
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} nftAddr
 * @param {bigint} fromBlock
 */
async function fetchStakedTokenIdsFromAlchemyTransfers(
  apiKey,
  user,
  stakingAddr,
  nftAddr,
  fromBlock
) {
  const fromHex = `0x${fromBlock.toString(16)}`;
  const [inbound, outbound] = await Promise.all([
    fetchTokenIdsFromAlchemyTransferDirection(apiKey, {
      fromBlock: fromHex,
      toBlock: "latest",
      fromAddress: user,
      toAddress: stakingAddr,
      contractAddresses: [nftAddr],
    }),
    fetchTokenIdsFromAlchemyTransferDirection(apiKey, {
      fromBlock: fromHex,
      toBlock: "latest",
      fromAddress: stakingAddr,
      toAddress: user,
      contractAddresses: [nftAddr],
    }),
  ]);
  /** @type {Set<string>} */
  const ids = new Set(inbound);
  for (const id of outbound) ids.delete(id);
  return [...ids];
}

function sortNumericTokenIds(ids) {
  return [...ids].sort((a, b) => {
    const na = BigInt(a);
    const nb = BigInt(b);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  });
}

/** Keep rows whose on-chain owner matches `user` and sort ids numerically. */
function narrowStakedRoostForUser(infos, user) {
  const userLc = user.toLowerCase();
  const stakedRows = infos.filter((row) => row.owner && row.owner.toLowerCase() === userLc);
  const tokenIds = sortNumericTokenIds(stakedRows.map((r) => r.tokenId));
  const byId = new Map(stakedRows.map((r) => [r.tokenId, r]));
  const stakeRows = tokenIds.map((id) => byId.get(id)).filter(Boolean);
  return { tokenIds, stakeRows };
}

/**
 * Cache hit: skip eth_getLogs; refresh stakeInfo rows only (unstake countdown moves).
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 * @param {string[]} cachedIds
 */
async function roostFromCachedTokenIds(config, stakingAddr, user, cachedIds) {
  if (cachedIds.length === 0) return { tokenIds: [], stakeRows: [] };
  const infos = await fetchStakeInfos(config, stakingAddr, cachedIds);
  return narrowStakedRoostForUser(infos, user);
}

/**
 * Reconstruct currently staked token IDs + stake rows. Log scans + optional Alchemy; `stakeInfo` is truth.
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{ tokenIds: string[], stakeRows: object[] }>}
 */
export async function fetchStakedTokenIds(config, stakingAddr, user, options = {}) {
  const fromBlock = deployBlockFromEnv();
  const env = getStakingEnv();
  const cacheKey = roostCacheKey(user, stakingAddr, env.owlexNft);

  if (!options.force) {
    const hit = roostIdsCache.get(cacheKey);
    if (hit && Date.now() - hit.t < ROOST_IDS_CACHE_MS) {
      return roostFromCachedTokenIds(config, stakingAddr, user, hit.ids);
    }
  }

  const inflight = roostFetchInFlight.get(cacheKey);
  if (inflight) return inflight;

  const run = (async () => {
    try {
      const useAlchemyTransfers =
        env.roostAlchemyTransfers &&
        env.alchemyApiKey.length > 0 &&
        isHexAddress(env.owlexNft);
      const idsFromAlchemy = useAlchemyTransfers
        ? await fetchStakedTokenIdsFromAlchemyTransfers(
            env.alchemyApiKey,
            user,
            stakingAddr,
            /** @type {`0x${string}`} */ (env.owlexNft),
            fromBlock
          )
        : [];

      /** Owlex NFTs held by the staking contract right now (Alchemy). Source of truth for token ids to check with stakeInfo — log replay alone misses restake-after-unstake edge cases. */
      let idsHeldByStaking = [];
      if (env.alchemyApiKey.length > 0 && isHexAddress(env.owlexNft)) {
        try {
          idsHeldByStaking = await fetchOwnedTokenIds(
            stakingAddr,
            env.owlexNft,
            env.alchemyApiKey
          );
        } catch (e) {
          console.warn("[owlex-staking] Alchemy getNFTsForOwner(staking contract) failed", e);
        }
      }

      const alchemyOnlyMode = env.roostAlchemySupplement === "alchemy_only";
      const shouldRunRpcRecon =
        (!useAlchemyTransfers || idsFromAlchemy.length === 0 || !alchemyOnlyMode) &&
        (idsHeldByStaking.length === 0 || options.force === true);
      const [idsFromLogs, idsFromNftTransfers] = shouldRunRpcRecon
        ? await Promise.all([
            fetchStakedTokenIdsFromLogs(config, stakingAddr, user, fromBlock),
            isHexAddress(env.owlexNft)
              ? fetchStakedTokenIdsFromNftTransfers(
                  config,
                  /** @type {`0x${string}`} */ (env.owlexNft),
                  user,
                  stakingAddr,
                  fromBlock
                )
              : Promise.resolve([]),
          ])
        : [[], []];

      /** @type {Set<string>} */
      const candidates = new Set(idsFromAlchemy);
      for (const id of idsHeldByStaking) candidates.add(id);
      for (const id of idsFromLogs) candidates.add(id);
      for (const id of idsFromNftTransfers) candidates.add(id);

      let list = [...candidates];
      if (list.length > MAX_STAKEINFO_CANDIDATES) {
        console.warn(
          `[owlex-staking] stakeInfo candidates capped (${list.length} → ${MAX_STAKEINFO_CANDIDATES})`
        );
        list = sortNumericTokenIds(list).slice(0, MAX_STAKEINFO_CANDIDATES);
      }

      if (list.length === 0) {
        roostIdsCache.set(cacheKey, { t: Date.now(), ids: [] });
        return { tokenIds: [], stakeRows: [] };
      }

      const infos = await fetchStakeInfos(config, stakingAddr, list);
      const { tokenIds, stakeRows } = narrowStakedRoostForUser(infos, user);
      roostIdsCache.set(cacheKey, { t: Date.now(), ids: tokenIds });
      return { tokenIds, stakeRows };
    } finally {
      if (roostFetchInFlight.get(cacheKey) === run) roostFetchInFlight.delete(cacheKey);
    }
  })();

  roostFetchInFlight.set(cacheKey, run);
  return run;
}

/** Large `stakeInfo` multicalls often fail partially on public RPCs — stay well under typical limits. */
const STAKEINFO_MULTICALL_CHUNK = 48;

/**
 * @param {string} tokenId
 * @param {import('@wagmi/core').Address} owner
 * @param {bigint} stakedAt
 */
function stakeInfoRow(tokenId, owner, stakedAt) {
  const st = BigInt(stakedAt);
  const unstakeReadyAt = st + STAKE_DURATION_SEC;
  const now = BigInt(Math.floor(Date.now() / 1000));
  return {
    tokenId,
    owner,
    stakedAt: st,
    unstakeReadyAt,
    canUnstake: now >= unstakeReadyAt,
  };
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {string[]} tokenIds
 */
export async function fetchStakeInfos(config, stakingAddr, tokenIds) {
  if (tokenIds.length === 0) return [];

  /** @type {import('@wagmi/core').MulticallReturnType} */
  const results = [];

  for (let i = 0; i < tokenIds.length; i += STAKEINFO_MULTICALL_CHUNK) {
    const chunk = tokenIds.slice(i, i + STAKEINFO_MULTICALL_CHUNK);
    const contracts = chunk.map((id) => ({
      address: stakingAddr,
      abi: stakingReadAbi,
      functionName: "stakeInfo",
      args: [BigInt(id)],
    }));
    const part = await multicall(config, {
      chainId: 1,
      contracts,
      allowFailure: true,
    });
    results.push(...part);
  }

  /** @type {ReturnType<typeof stakeInfoRow>[]} */
  const rows = tokenIds.map((id, i) => {
    const r = results[i];
    if (r.status !== "success") {
      return {
        tokenId: id,
        owner: null,
        stakedAt: null,
        unstakeReadyAt: null,
        canUnstake: false,
      };
    }
    const [owner, stakedAt] = r.result;
    return stakeInfoRow(id, owner, stakedAt);
  });

  const failedIdx = results
    .map((r, i) => (r.status !== "success" ? i : -1))
    .filter((i) => i >= 0);

  await Promise.all(
    failedIdx.map(async (i) => {
      const id = tokenIds[i];
      try {
        const res = await readContract(config, {
          address: stakingAddr,
          abi: stakingReadAbi,
          functionName: "stakeInfo",
          args: [BigInt(id)],
          chainId: 1,
        });
        const [owner, stakedAt] = res;
        rows[i] = stakeInfoRow(id, owner, stakedAt);
      } catch (e) {
        console.warn("[owlex-staking] stakeInfo fallback read failed", id, e);
      }
    })
  );

  return rows;
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} xpAddr
 * @param {`0x${string}`} user
 */
export async function fetchXpBalanceWei(config, xpAddr, user) {
  try {
    return await readContract(config, {
      address: xpAddr,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [user],
      chainId: 1,
    });
  } catch (err) {
    console.error("[owlex-staking] fetchXpBalanceWei", err);
    return 0n;
  }
}

/**
 * @param {bigint} wei
 */
export function formatOwxp(wei) {
  try {
    return formatUnits(wei, 18);
  } catch {
    return "0";
  }
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 */
export async function readStakingPaused(config, stakingAddr) {
  try {
    return await readContract(config, {
      address: stakingAddr,
      abi: stakingAbi,
      functionName: "paused",
      chainId: 1,
    });
  } catch {
    return true;
  }
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 */
export async function readCanStake(config, stakingAddr, user) {
  try {
    return await readContract(config, {
      address: stakingAddr,
      abi: stakingAbi,
      functionName: "canStake",
      args: [user],
      chainId: 1,
    });
  } catch {
    return false;
  }
}

/**
 * Unix timestamp (seconds) when the user may stake again; `0` if no prior unstake cooldown.
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} stakingAddr
 * @param {`0x${string}`} user
 */
export async function readCooldownEndsAt(config, stakingAddr, user) {
  try {
    return await readContract(config, {
      address: stakingAddr,
      abi: stakingAbi,
      functionName: "cooldownEndsAt",
      args: [user],
      chainId: 1,
    });
  } catch {
    return 0n;
  }
}

/**
 * @param {import('@wagmi/core').Config} config
 * @param {`0x${string}`} nftAddr
 * @param {`0x${string}`} owner
 * @param {`0x${string}`} stakingAddr
 */
export async function readIsApprovedForAll(config, nftAddr, owner, stakingAddr) {
  try {
    return await readContract(config, {
      address: nftAddr,
      abi: erc721Abi,
      functionName: "isApprovedForAll",
      args: [owner, stakingAddr],
      chainId: 1,
    });
  } catch {
    return false;
  }
}

/** @returns {{ staking: `0x${string}` | null, xp: `0x${string}` | null }} */
export function stakingAddressesOrNull() {
  const env = getStakingEnv();
  if (!isHexAddress(env.stakingContract) || !isHexAddress(env.owlexXp)) {
    return { staking: null, xp: null };
  }
  return {
    staking: /** @type {`0x${string}`} */ (env.stakingContract),
    xp: /** @type {`0x${string}`} */ (env.owlexXp),
  };
}
